import { Box } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  TextField,
} from '@verantos-dev/shared-components';

import { useRenameAnalyticalVariableDialog } from '../hooks/useRenameAnalyticalVariableDialog';

export interface RenameAnalyticalVariableDialogProps {
  analyticalVariableId: string;
  currentName: string;
  onClose: () => void;
  /** Project this rename was triggered from, when known — see
   * useRenameAvMutation's ``projectId`` doc for why it matters. Omit for
   * the org-wide AV list page. */
  projectId?: string;
}

/**
 * Rename dialog for an analytical variable's display name (OR-6090). Shared
 * by the AV list page and the project artifact list row actions — both open
 * this same dialog; the variable detail page uses inline editing instead
 * (see ``VariableNameInlineEdit``). Mirrors ``RenameAnalyticalDatasetDialog``.
 */
export const RenameAnalyticalVariableDialog = ({
  analyticalVariableId,
  currentName,
  onClose,
  projectId,
}: RenameAnalyticalVariableDialogProps) => {
  const { formatMessage } = useIntl();
  const {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError,
    isValidToSubmit,
    hasInvalidCharacters,
  } = useRenameAnalyticalVariableDialog({
    analyticalVariableId,
    currentName,
    onClose,
    projectId,
  });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({
      id: 'projects.analyticalVariables.renameModal.cancelButton',
    }),
    entrySaveButtonText: formatMessage({
      id: 'projects.analyticalVariables.renameModal.renameButton',
    }),
    submittingCancelButtonText: formatMessage({
      id: 'projects.analyticalVariables.renameModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({
      id: 'projects.analyticalVariables.renameModal.submitting',
    }),
    successCloseButtonText: formatMessage({
      id: 'projects.analyticalVariables.renameModal.closeButton',
    }),
    submittingErrorTitle: formatMessage({
      id: 'projects.analyticalVariables.renameModal.submittingError',
    }),
    successTitle: formatMessage({ id: 'projects.analyticalVariables.renameModal.success.title' }),
    successText: formatMessage(
      { id: 'projects.analyticalVariables.renameModal.success.text' },
      { name }
    ),
  };

  return (
    <ApiWorkflowDialog
      id="renameAnalyticalVariableDialog"
      title={formatMessage({ id: 'projects.analyticalVariables.renameModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      isValidForSubmit={isValidToSubmit}
      isSubmitting={isPending}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <TextField
          id="rename-analytical-variable-name-input"
          displayLabel={formatMessage({
            id: 'projects.analyticalVariables.renameModal.name.label',
          })}
          value={name}
          placeholder={formatMessage({
            id: 'projects.analyticalVariables.renameModal.name.placeholder',
          })}
          onChange={onChangeName}
          error={hasInvalidCharacters}
          helperText={
            hasInvalidCharacters
              ? formatMessage({
                  id: 'projects.analyticalVariables.renameModal.invalidCharacterErrorMsg',
                })
              : undefined
          }
          width="auto"
        />
      </Box>
    </ApiWorkflowDialog>
  );
};

export default RenameAnalyticalVariableDialog;
